import { useId, useState, type ReactNode, type SyntheticEvent } from 'react';
import { ApiError } from '../api/client';

interface FieldProps {
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'password';
  autoComplete?: string;
  error?: string | undefined;
  hint?: ReactNode;
}

/** A labelled input whose server-side error is announced and tied to it. */
export function Field({ label, name, value, onChange, type = 'text', autoComplete, error, hint }: FieldProps) {
  const id = useId();
  const describedBy = [error ? `${id}-error` : null, hint ? `${id}-hint` : null].filter(Boolean).join(' ');
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        name={name}
        type={type}
        value={value}
        autoComplete={autoComplete}
        onChange={(e) => { onChange(e.target.value); }}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
      />
      {hint && <p id={`${id}-hint`} className="field-hint">{hint}</p>}
      {error && <p id={`${id}-error`} className="field-error">{error}</p>}
    </div>
  );
}

export function FormError({ message }: { message: string | null }) {
  return message ? <p role="alert" className="form-error">{message}</p> : null;
}

/**
 * Runs a form's request: tracks the in-flight state and splits a failure into field
 * errors (422 details) and one message for everything else.
 */
export function useSubmit<T>(action: () => Promise<T>, onSuccess: (result: T) => void) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    setPending(true);
    setError(null);
    action().then(
      (result) => { setPending(false); onSuccess(result); },
      (err: unknown) => { setPending(false); setError(err instanceof Error ? err : new Error('Something went wrong.')); },
    );
  };
  const fieldErrors = error instanceof ApiError ? error.fieldErrors() : {};
  const formError = error && Object.keys(fieldErrors).length === 0 ? error.message : null;
  return { submit, pending, fieldErrors, formError };
}
